import * as React                       from 'react'
import { addLocaleData, IntlProvider, } from 'react-intl'
import * as en                          from 'react-intl/locale-data/en'
import * as es                          from 'react-intl/locale-data/es'
import { connect }                      from 'react-redux'

import Snackbar from '@material-ui/core/Snackbar'

import { Locale, selectLocale } from '../../redux'
import hooks                    from '../../testing-hooks'

import enJson from './translations/en.json'
import esJson from './translations/es.json'

addLocaleData([ ...en, ...es ])

/*
 These translations are shown the moment the locale changes
 which means that the component that shows it needs to react
 to a locale preference change.

 Because the same component (this one) injects the application
 translations depending on the locale state it can't inject
 the translation of the change itself into the app (or at least
 I think it makes a lot more sense to have it controlled in the
 same component rather than passing the 'isSnackbarOpen' state
 down to a dumb snackbar component)

 This is why these are the only translations handled directly
 in the source code rather than in json files. Makes sense?
 */
const localeChangedTranslations = {
  en: 'Language preference set to english',
  es: 'Preferencia de idioma establecida en español',
}

export const locales: Locale [] = [
  Locale.EN,
  Locale.ES,
]

export const localeNames: { [key in Locale]: string} = {
  [ Locale.EN ]: 'English',
  [ Locale.ES ]: 'Español',
}

export interface LanguageContext {
  locale: Locale,
  locales: typeof locales,
  localeNames: typeof localeNames,
}

const LanguageModificationContext = React.createContext({})
const { Provider } = LanguageModificationContext
export const Consumer = LanguageModificationContext.Consumer

const mstp = state => ({ locale: selectLocale(state) })

type LanguageProps = ReturnType<typeof mstp>

interface State { isSnackbarOpen: boolean }

class Language extends React.Component<LanguageProps, State> {
  /*
   setting defaultProps in state initiation because using static defaultProps
   doesn't work with typescript since those only get assigned during runtime
   */
  public state = {
    isSnackbarOpen: false,
  }

  public render () {
    const { locale } = this.props

    const messages = {
      [ Locale.EN ]: enJson,
      [ Locale.ES ]: esJson,
    }[ locale ]

    const context = {
      locale,
      locales,
      localeNames,
    }

    const languageUpdatedSnackBar = (
      <Snackbar
        autoHideDuration={4000}
        onClose={this.hideSnackbar}
        open={this.state.isSnackbarOpen}
        message={(
          <span data-test-id={hooks.languageChangedSnackBar}>
            {localeChangedTranslations[ locale ]}
          </span>
        )}
        anchorOrigin={{
          vertical:   'bottom',
          horizontal: 'left',
        }}
      />
    )

    return (
      <>
        <Provider value={context}>
          <IntlProvider
            locale={locale}
            messages={messages}
          >
            {this.props.children}
          </IntlProvider>
        </Provider>
        {languageUpdatedSnackBar}
      </>
    )
  }

  public componentDidUpdate (prevProps: LanguageProps) {
    if (prevProps.locale !== this.props.locale) {
      this.setState({
        isSnackbarOpen: true,
      })
    }
  }

  private hideSnackbar = () => {
    this.setState({
      isSnackbarOpen: false,
    })
  }
}

export default connect(mstp)(Language)



// WEBPACK FOOTER //
// ./src/contexts/Language/Language.tsx